import React, { useState } from 'react';

import { ActionIcon, Flex, Paper, ScrollArea, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconBrain, IconEdit, IconSearch, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NoDataFound } from 'components/NoDataFound';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { CreateMemoryButton } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryButton';
import { CreateMemoryModal } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryModal/CreateMemoryModal';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface MemoriesManagementSectionProps {
  smartAssistant: SmartAssistant;
}

export function MemoriesManagementSection({ smartAssistant }: MemoriesManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [memoryBeingEditedIndex, setMemoryBeingEditedIndex] = useState<number | undefined>(undefined);
  const [memoryBeingDeletedIndex, setMemoryBeingDeletedIndex] = useState<number | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string>('');

  const memories = smartAssistant.memories || [];

  const filteredMemories = memories.filter((memory) =>
    memory.genericMemoryContent.content.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const deleteMemoryMutation = useMutation({
    mutationFn: (memoryIndex: number) => {
      setMemoryBeingDeletedIndex(memoryIndex);
      const updatedMemories = memories.filter((_, i) => i !== memoryIndex);
      return SmartAssistantService.updateSmartAssistant({
        id: smartAssistant._id,
        memories: updatedMemories
      });
    },
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      NotificationService.showResult(true, 'FEED_BACK.MEMORY.SUCCESS.DELETE');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.DELETE'),
    onSettled: () => {
      setMemoryBeingDeletedIndex(undefined);
    }
  });

  return (
    <ManageSmartAssistantSectionControlWrapper title={t('SMART_ASSISTANT.MEMORIES_SECTION.TITLE')}>
      <Stack spacing="md" w="100%" h="100%">
        <Flex justify="space-between" align="center" gap="md">
          <TextInput
            icon={<IconSearch size={16} />}
            placeholder={t('SMART_ASSISTANT.MEMORIES_SECTION.SEARCH_PLACEHOLDER')}
            style={{ flexGrow: 1 }}
            value={searchFilter}
            onChange={(event) => setSearchFilter(event.target.value)}
          />
          <CreateMemoryButton smartAssistant={smartAssistant} variant="filled" />
        </Flex>

        <ScrollArea scrollbarSize={0} style={{ flexGrow: 1 }}>
          <Stack spacing="md" h="100%">
            {memories.length === 0 && (
              <NoDataFound
                h="100%"
                size="xs"
                icon={<IconBrain />}
                title={t('SMART_ASSISTANT.MEMORIES_SECTION.NO_MEMORIES')}
                subTitle={t('SMART_ASSISTANT.MEMORIES_SECTION.NO_MEMORIES_SUBTITLE')}
              />
            )}

            {filteredMemories.length > 0 &&
              filteredMemories.map((memory, memoryIndex) => (
                <Paper key={memoryIndex} p="md" radius="md" withBorder>
                  <Flex justify="space-between" align="center" gap="md">
                    <Stack spacing={4} style={{ flex: 1 }}>
                      <Text size="sm" lineClamp={4}>
                        {memory.genericMemoryContent.content}
                      </Text>
                    </Stack>

                    <Flex gap={4} ml="md">
                      <Tooltip label={t('APP.ACTIONS.EDIT')} withArrow>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => setMemoryBeingEditedIndex(memoryIndex)}
                          color="gray">
                          <IconEdit size={18} />
                        </ActionIcon>
                      </Tooltip>

                      <Tooltip label={t('APP.ACTIONS.DELETE')} withArrow color="red">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => deleteMemoryMutation.mutate(memoryIndex)}
                          color="red"
                          loading={memoryBeingDeletedIndex === memoryIndex}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Flex>
                  </Flex>
                </Paper>
              ))}
          </Stack>
        </ScrollArea>
      </Stack>

      <CreateMemoryModal
        opened={memoryBeingEditedIndex !== undefined}
        onClose={() => setMemoryBeingEditedIndex(undefined)}
        smartAssistant={smartAssistant}
        memoryBeingEditedIndex={memoryBeingEditedIndex}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
